const enum XHR_STATUSES {
    UNSENT = 0, // initial state
    OPENED = 1, // open called
    HEADERS_RECEIVED = 2, // response headers received
    LOADING = 3, // response is loading (a data packet is received)
    DONE = 4, // request complete
}

type RequestMethods = "GET" | "POST" | "PUT";

interface ApiOptions {
    async: boolean,
    urlPrefix: string,
    responseType: XMLHttpRequestResponseType,
}

interface apiClientParams {
    async?: boolean,
    urlPrefix?: string,
    responseType?: XMLHttpRequestResponseType,
}

interface requestParams {
    url: string,
    method: RequestMethods,
    params?: unknown,
    data?: unknown
}

interface ApiResponse {
    data: any
}

export class ApiClient {
    private xhr = new XMLHttpRequest();
    private csrf: string;

    private options: ApiOptions = {
        async: true,
        urlPrefix: "/api/",
        responseType: "json"
    }

    constructor(private params?: apiClientParams) {
        this.options = { ...this.options, ...this.params };
        this.csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";
    }

    private sendRequest = <T extends ApiResponse>(method: RequestMethods, url: string, body?: unknown): Promise<T> => {
        return new Promise((resolve, reject) => {
            this.xhr.open(method, url, this.options.async);

            this.xhr.responseType = this.options.responseType;
            this.xhr.setRequestHeader("Accept", "application/json");
            this.xhr.setRequestHeader("Content-Type", "application/json");
            this.xhr.setRequestHeader("x-csrf-token", this.csrf);

            this.xhr.onload = function () {
                if (this.status >= 200 && this.status < 300) {
                    resolve({
                        data: this.response
                    } as T);
                } else {
                    reject(this);
                }
            };

            this.xhr.onerror = function () {
                reject(this);
            };

            // this.xhr.onload = function () { };
            // this.xhr.onreadystatechange = function () { };

            this.xhr.send(JSON.stringify(body));
        });
    }

    public get = (url: string, params: Document | XMLHttpRequestBodyInit | null | undefined | unknown) => {
        return this.sendRequest("GET", url, params);
    }

    public request = (data: requestParams): Promise<ApiResponse> => {
        let queryString = "";
        let body: unknown;
        if (data.params) {
            queryString = "?" + this.parseParams(data.params);
        }
        if (data.data) {
            body = data.data;
        }

        return this.sendRequest(
            data.method,
            this.options.urlPrefix + data.url + queryString,
            body
        );
    }

    private parseParams = <T>(obj: T, group = ""): string => {
        if (obj) {
            const queryString = Object.keys(obj).map((key) => {
                let isParent = false;
                let qs = obj[key];
                if (typeof qs !== "string") {
                    qs = this.parseParams(qs, key);
                    isParent = true;
                }
                if (group !== "") {
                    return group + "[" + key + "]" + '=' + qs;
                } else {
                    return isParent ? qs : key + '=' + qs;
                }
            });

            return queryString.join('&');
        }
        return "";
    }

    public jsonToQueryString(jsonString: string): string {
        try {
          // Parse the JSON string into an object
          const jsonObject = JSON.parse(jsonString);
      
          // Ensure that the parsed object is indeed an object
          if (typeof jsonObject !== 'object' || jsonObject === null) {
            throw new Error('Invalid JSON object');
          }
      
          // Map over the object's entries and encode them
          const queryString = Object.entries(jsonObject)
            .map(([key, value]) => {
              // Ensure the value is a string or can be coerced into a string
              const stringValue = value != null ? String(value) : '';
              return `${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`;
            })
            .join('&');
      
          return queryString;
        } catch (error) {
          console.error('Error parsing JSON string:', error);
          return '';
        }
      }

    public setPrefix(prefix: string) {
        this.options.urlPrefix = prefix;
    }
}


export const defaultApiClient = new ApiClient();